import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Field } from '@/components/ui/Field';
import { useDebounced } from '@/lib/useDebounced';

import type { LinkRow } from './pageLabels';
import { useDocumentChoices } from './usePages';

interface DocumentPickerDialogProps {
  /** The documents the page already links, shown as "Linked". */
  linkedIds: readonly string[];
  /** From the reply's `limits` (P3, P31). */
  maxLinkedDocuments: number;
  onPick: (link: LinkRow) => void;
  onClose: () => void;
}

const TEST_ID = 'document-picker-dialog';

/**
 * The organization's published documents, for Linked Content (P14, PNF3):
 * searched and paged by the server twenty at a time, the search settled
 * after a pause so a word is one request. Several can be added before Done;
 * a linked one reads "Linked", and nothing more can be added at the limit.
 */
export function DocumentPickerDialog({
  linkedIds,
  maxLinkedDocuments,
  onPick,
  onClose,
}: DocumentPickerDialogProps) {
  const [draft, setDraft] = useState('');
  const settled = useDebounced(draft.trim(), 300);
  const [page, setPage] = useState(1);
  // A new search starts from its first page.
  const [searched, setSearched] = useState(settled);
  if (searched !== settled) {
    setSearched(settled);
    setPage(1);
  }

  const choices = useDocumentChoices(settled, page);
  const rows = choices.data?.items ?? [];
  const hasMore = choices.data?.pagination.has_more ?? false;
  const atLimit = linkedIds.length >= maxLinkedDocuments;

  let body;
  if (choices.isError) {
    body = (
      <ErrorState
        title="The documents could not be loaded."
        onRetry={() => void choices.refetch()}
        testId={TEST_ID}
      />
    );
  } else if (choices.isPending) {
    body = (
      <Box data-testid={`${TEST_ID}-loading`} sx={{ py: 2 }}>
        <LinearProgress aria-label="Loading documents" />
      </Box>
    );
  } else if (rows.length === 0) {
    body = <EmptyState title="No published documents match" testId={TEST_ID} />;
  } else {
    body = (
      <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
        {rows.map((row) => {
          const linked = linkedIds.includes(row.resource_id);
          return (
            <Box
              component="li"
              key={row.resource_id}
              data-testid={`${TEST_ID}-option-${row.resource_id}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                py: 1,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Typography sx={{ overflowWrap: 'anywhere' }}>{row.title}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {linked ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    data-testid={`${TEST_ID}-option-${row.resource_id}-linked-label`}
                  >
                    Linked
                  </Typography>
                ) : null}
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={linked || atLimit}
                  aria-label={`Add ${row.title}`}
                  onClick={() =>
                    onPick({
                      resourceType: 'DOCUMENT',
                      resourceId: row.resource_id,
                      title: row.title,
                      available: true,
                    })
                  }
                  data-testid={`${TEST_ID}-option-${row.resource_id}-add-button`}
                >
                  Add
                </Button>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title="Add a document"
      description="Published documents of your organization."
      testId={TEST_ID}
      actions={
        <Button onClick={onClose} data-testid={`${TEST_ID}-done-button`}>
          Done
        </Button>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Field label="Search published documents" testId={`${TEST_ID}-search`}>
          <TextField
            type="search"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          />
        </Field>

        {atLimit ? (
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid={`${TEST_ID}-limit-value`}
          >
            Up to {maxLinkedDocuments} documents
          </Typography>
        ) : null}

        {body}

        {page > 1 || hasMore ? (
          <Box
            component="nav"
            aria-label="Document pages"
            data-testid={`${TEST_ID}-pagination`}
            sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}
          >
            <Button
              size="sm"
              variant="ghost"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              data-testid={`${TEST_ID}-previous-button`}
            >
              Previous page
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={!hasMore}
              onClick={() => setPage(page + 1)}
              data-testid={`${TEST_ID}-next-button`}
            >
              Next page
            </Button>
          </Box>
        ) : null}
      </Box>
    </Dialog>
  );
}
